import { useState } from 'react'
import { FileText, Image, FileSpreadsheet, File, Download, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGenerateEvidenceDownloadUrl,
  useRemoveEvidence,
  type BonusAssessment,
} from '@/api/bonificacao-hooks'
import type { EvidenceFileMetadata } from './types'

export interface IndicatorEvidenceListProps {
  assessmentId: string
  indicadorId: string
  files: EvidenceFileMetadata[]
  canDelete: boolean
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return FileText
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return FileText
  if (mimeType.startsWith('image/')) return Image
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'text/csv'
  ) return FileSpreadsheet
  return File
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function truncateFileName(name: string, maxLen = 40): string {
  if (name.length <= maxLen) return name
  const ext = name.lastIndexOf('.')
  if (ext > 0 && name.length - ext <= 6) {
    const extPart = name.slice(ext)
    const namePart = name.slice(0, maxLen - extPart.length - 1)
    return `${namePart}…${extPart}`
  }
  return `${name.slice(0, maxLen - 1)}…`
}

export function IndicatorEvidenceList({
  assessmentId,
  indicadorId,
  files,
  canDelete,
}: IndicatorEvidenceListProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const downloadMutation = useGenerateEvidenceDownloadUrl()
  const removeMutation = useRemoveEvidence()

  if (!files || files.length === 0) return null

  const handleDownload = async (evidenceId: string) => {
    setLoadingId(evidenceId)
    try {
      const { downloadUrl } = await downloadMutation.mutateAsync({
        assessmentId,
        indicadorId,
        evidenceId,
      })
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Falha ao gerar link de download.')
    } finally {
      setLoadingId(null)
    }
  }

  const handleRemove = async (evidenceId: string) => {
    // Optimistic update
    const previousData = queryClient.getQueryData<BonusAssessment>(
      ['bonus', 'getAssessment', { assessmentId }]
    )

    queryClient.setQueryData<BonusAssessment>(
      ['bonus', 'getAssessment', { assessmentId }],
      (old) => {
        if (!old) return old
        const newDados = { ...old.dados }
        const indicadorDados = newDados[indicadorId]
        if (indicadorDados?.evidenceFiles) {
          newDados[indicadorId] = {
            ...indicadorDados,
            evidenceFiles: indicadorDados.evidenceFiles.filter(
              (f) => f.evidenceId !== evidenceId
            ),
          }
        }
        return { ...old, dados: newDados }
      }
    )

    try {
      await removeMutation.mutateAsync({
        assessmentId,
        indicadorId,
        evidenceId,
      })
      toast.success('Evidência removida.')
    } catch (error) {
      console.error('Remove failed:', error)
      // Rollback optimistic update
      if (previousData) {
        queryClient.setQueryData(
          ['bonus', 'getAssessment', { assessmentId }],
          previousData
        )
      }
      toast.error('Falha ao remover evidência.')
    }
  }

  return (
    <div
      className="space-y-1"
      data-testid={`evidence-list-${indicadorId}`}
    >
      {files.map((file) => {
        const IconComponent = getFileIcon(file.mimeType)
        const isLoading = loadingId === file.evidenceId

        return (
          <div
            key={file.evidenceId}
            className="flex items-center gap-2 py-1 px-2 rounded hover:bg-brand-dark-light/50 group"
            data-testid={`evidence-file-${file.evidenceId}`}
          >
            <IconComponent className="w-4 h-4 text-brand-gray flex-shrink-0" />
            <span className="text-xs text-white flex-1 truncate" title={file.fileName}>
              {truncateFileName(file.fileName)}
            </span>
            <span className="text-xs text-brand-gray whitespace-nowrap">
              {formatFileSize(file.fileSize)}
            </span>
            <button
              type="button"
              className="p-1 opacity-0 group-hover:opacity-100 hover:text-brand-primary transition-all"
              title="Download"
              disabled={isLoading}
              data-testid={`evidence-download-${file.evidenceId}`}
              onClick={() => handleDownload(file.evidenceId)}
            >
              {isLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
            </button>
            {canDelete && (
              <button
                type="button"
                className="p-1 opacity-0 group-hover:opacity-100 hover:text-brand-red transition-all"
                title="Remover"
                data-testid={`evidence-remove-${file.evidenceId}`}
                onClick={() => handleRemove(file.evidenceId)}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
